<template>
  <v-dialog v-model="dialog" max-width="720" persistent>
    <v-card class="approval-wizard" :class="{ 'approval-wizard-dark': isDark }">
      <v-card-title class="approval-title">
        <v-icon :color="isApproval ? 'success' : 'error'" class="mr-2">
          {{ isApproval ? "mdi-check-decagram-outline" : "mdi-archive-arrow-down-outline" }}
        </v-icon>
        {{ isApproval ? "Aprovar orcamento" : "Reprovar orcamento" }}
      </v-card-title>

      <v-card-text>
        <div class="approval-sale">
          <strong>{{ clientName }}</strong>
          <span>Venda #{{ sale.id_transacao || sale._id }}</span>
        </div>

        <template v-if="isApproval">
          <v-autocomplete
            dense
            outlined
            clearable
            label="Obra vinculada"
            item-text="nome"
            return-object
            :items="works"
            v-model="form.work"
            :loading="loadingRefs"
          />

          <v-autocomplete
            dense
            outlined
            label="Polo de producao"
            item-text="nome"
            return-object
            :items="unities"
            v-model="form.production_unity"
            :loading="loadingRefs"
            :rules="[required]"
          />

          <div v-if="form.work && form.work._id" class="work-details">
            <span class="section-label">Dados de acompanhamento da obra</span>
            <v-text-field
              dense
              outlined
              label="Responsavel interno"
              v-model="form.work_details.responsible_name"
            />
            <v-layout wrap>
              <v-flex xs12 md6 class="pr-md-2">
                <v-text-field
                  dense
                  outlined
                  label="Contato da obra"
                  v-model="form.work_details.contact_name"
                />
              </v-flex>
              <v-flex xs12 md6>
                <v-text-field
                  dense
                  outlined
                  label="Telefone do contato"
                  v-model="form.work_details.contact_phone"
                />
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex xs12 md6 class="pr-md-2">
                <v-text-field
                  dense
                  outlined
                  label="Engenheiro"
                  v-model="form.work_details.engineer_name"
                />
              </v-flex>
              <v-flex xs12 md6>
                <v-text-field
                  dense
                  outlined
                  label="Telefone do engenheiro"
                  v-model="form.work_details.engineer_phone"
                />
              </v-flex>
            </v-layout>
          </div>

          <v-checkbox
            dense
            hide-details
            class="mt-0"
            label="Enviar solicitacao de aprovacao pelo IntraWhats quando houver conversa ativa"
            v-model="form.send_whatsapp"
          />
        </template>

        <v-textarea
          v-else
          outlined
          rows="3"
          label="Motivo da reprovacao"
          v-model="form.reason"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn
          :color="isApproval ? 'success' : 'error'"
          dark
          :loading="submitting"
          @click="submit"
        >
          {{ isApproval ? "Aprovar" : "Reprovar" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";

function idOf(value) {
  return value && (value._id || value.id || value);
}

export default {
  props: {
    sale: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialog: false,
      decision: "approved",
      loadingRefs: false,
      submitting: false,
      unities: [],
      works: [],
      form: this.defaultForm()
    };
  },
  computed: {
    isApproval() {
      return this.decision === "approved";
    },
    isDark() {
      return localStorage.dark === "true";
    },
    clientName() {
      const client = this.sale.compra && this.sale.compra.cliente || this.sale.client || {};
      return client.nome || "Cliente nao informado";
    }
  },
  watch: {
    "form.work"(work) {
      if (work && work.unity && !this.form.production_unity) {
        const unityId = idOf(work.unity);
        this.form.production_unity = this.unities.find(item => idOf(item) === unityId) || work.unity;
      }
    }
  },
  methods: {
    ...mapActions(["set_approval_gestordepedido", "createGlobalMessage"]),
    defaultForm() {
      return {
        work: null,
        production_unity: null,
        reason: "",
        send_whatsapp: false,
        work_details: {
          responsible_name: "",
          contact_name: "",
          contact_phone: "",
          engineer_name: "",
          engineer_phone: ""
        }
      };
    },
    required(value) {
      return !!value || "Obrigatorio";
    },
    async open(decision) {
      this.decision = decision;
      this.form = this.defaultForm();
      this.dialog = true;
      await this.loadRefs();
      this.prefillFromSale();
    },
    close() {
      if (this.submitting) return;
      this.dialog = false;
    },
    async loadRefs() {
      this.loadingRefs = true;
      try {
        const [unities, works] = await Promise.all([
          this.$run("unidades/list", { limitPerPage: 2000 }),
          this.$run("obras/list", { limitPerPage: 2000 })
        ]);
        this.unities = unities.data.docs || [];
        this.works = works.data.docs || [];
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message: "Nao foi possivel carregar obras e polos.",
          timeout: 5000
        });
      } finally {
        this.loadingRefs = false;
      }
    },
    prefillFromSale() {
      const saleWork = this.sale.work || this.sale.compra && this.sale.compra.obra || null;
      const workId = idOf(saleWork);
      if (workId) {
        this.form.work = this.works.find(item => idOf(item) === workId) || saleWork;
      }

      const saleUnity = this.sale.production_unity || this.sale.unity || this.form.work && this.form.work.unity || null;
      const unityId = idOf(saleUnity);
      if (unityId) {
        this.form.production_unity = this.unities.find(item => idOf(item) === unityId) || saleUnity;
      }

      const details = this.sale.approval && this.sale.approval.work_details || {};
      this.form.work_details = {
        ...this.form.work_details,
        ...details
      };
    },
    async submit() {
      if (this.isApproval && !this.form.production_unity) {
        this.createGlobalMessage({
          type: "error",
          message: "Informe o polo de producao.",
          timeout: 4000
        });
        return;
      }

      this.submitting = true;
      try {
        const result = await this.set_approval_gestordepedido({
          _id: this.sale._id,
          decision: this.decision,
          work: this.form.work,
          production_unity: this.form.production_unity,
          work_details: this.form.work_details,
          reason: this.form.reason,
          send_whatsapp: this.form.send_whatsapp
        });
        this.$emit("saved", result.sale || result);
        this.dialog = false;
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.approval-wizard {
  border-radius: 8px;
}
.approval-wizard-dark {
  background: #111b21;
  color: #e9edef;
}
.approval-title {
  align-items: center;
  display: flex;
}
.approval-wizard-dark .approval-title {
  color: #eef4f1;
}
.approval-sale {
  background: #f6f7f8;
  border: 1px solid #e0e3e6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  padding: 12px;
}
.approval-sale strong {
  color: #1f2933;
}
.approval-sale span,
.section-label {
  color: #667085;
  font-size: 12px;
}
.approval-wizard-dark .approval-sale {
  background: #182229;
  border-color: #26343c;
}
.approval-wizard-dark .approval-sale strong {
  color: #eef4f1;
}
.approval-wizard-dark .approval-sale span,
.approval-wizard-dark .section-label {
  color: #9fb0ba;
}
.approval-wizard-dark ::v-deep .v-label,
.approval-wizard-dark ::v-deep .v-input input,
.approval-wizard-dark ::v-deep .v-input textarea,
.approval-wizard-dark ::v-deep .v-select__selection,
.approval-wizard-dark ::v-deep .v-messages {
  color: #dce6ea !important;
}
.approval-wizard-dark ::v-deep fieldset {
  border-color: #3b4d57 !important;
}
.approval-wizard-dark ::v-deep .v-input--is-focused fieldset {
  border-color: #25d366 !important;
}
.work-details {
  margin-top: 6px;
}
.section-label {
  display: block;
  margin-bottom: 8px;
}
</style>
